<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useHead } from '@unhead/vue'
import lx from '../utils/lx'
import { isMobileViewport } from '../utils/environment'
import { ensureMutedAutoplayUnlock } from '../utils/mutedAutoplay'
import { bindMvStayFromDom, type ModuleStayController } from '../utils/moduleStay'
import { runPageScripts, type PageScripts } from '../utils/pageScripts'
import {
  CAPABILITIES,
  CONTACT_EMAIL,
  FAQ_ITEMS
} from '../components/digital-live/constants'
import SiteNavbar from '../components/digital-live/SiteNavbar.vue'
import HeroSection from '../components/digital-live/HeroSection.vue'
import IconMarqueeSection from '../components/digital-live/IconMarqueeSection.vue'
import CapabilitySection from '../components/digital-live/CapabilitySection.vue'
import CtaBannerSection from '../components/digital-live/CtaBannerSection.vue'
import WhyDigitalHumanSection from '../components/digital-live/WhyDigitalHumanSection.vue'
import WhyMliveSection from '../components/digital-live/WhyMliveSection.vue'
import CaseSection from '../components/digital-live/CaseSection.vue'
import StepSection from '../components/digital-live/StepSection.vue'
import TechSection from '../components/digital-live/TechSection.vue'
import PatentSection from '../components/digital-live/PatentSection.vue'
import FaqSection from '../components/digital-live/FaqSection.vue'
import SiteFooter from '../components/digital-live/SiteFooter.vue'
import SideFloatDock from '../components/digital-live/SideFloatDock.vue'
import '../styles/digital-live.scss'

const isContactVisible = ref(false)
const copyStatus = ref<'idle' | 'success' | 'error'>('idle')
const isMobile = ref(isMobileViewport())

// 在 hydration/setup 阶段就监听 WeixinJSBridgeReady，避免 mounted 时事件已经错过。
if (typeof window !== 'undefined' && isMobile.value) ensureMutedAutoplayUnlock()

useHead({
  title: '美团智播-美团官方AI数字人直播平台',
  meta: [
    {
      name: 'description',
      content:
        '美团智播提供 AI 数字人、智能话术、实时互动与自动开播能力，帮助商家快速搭建 7×24 小时在线的智能直播间。'
    },
    { property: 'og:title', content: '美团智播 - AI 主播全天在线，生意不打烊' },
    { property: 'og:description', content: '美团 AI 数字人直播平台，让直播间成为全天候的生意入口。' },
    { property: 'og:type', content: 'website' }
  ]
})

let statusTimer: ReturnType<typeof setTimeout> | undefined
let pageScripts: PageScripts | undefined
let mvStayController: ModuleStayController | undefined

async function copyContact(): Promise<void> {
  isContactVisible.value = true
  const { Message } = await import('@ss/mtd-vue3')
  try {
    await navigator.clipboard.writeText(CONTACT_EMAIL)
    copyStatus.value = 'success'
    Message.success('已复制联系邮箱')
  } catch (error) {
    console.error('[Home] 复制联系邮箱失败：', error)
    copyStatus.value = 'error'
    Message.error('复制失败，请手动复制邮箱')
  }

  if (statusTimer) window.clearTimeout(statusTimer)
  statusTimer = window.setTimeout(() => {
    copyStatus.value = 'idle'
    isContactVisible.value = false
  }, 2400)
}

onMounted(async () => {
  lx.pageView('c_mtlive_b_zjsqtldb')
  isMobile.value = isMobileViewport()
  document.documentElement.classList.add('digital-live-page-active')
  if (isMobile.value) document.documentElement.classList.add('digital-live-touch')
  pageScripts = runPageScripts(isMobile.value)

  await nextTick()
  const pageRoot = document.querySelector('.digital-live-page')
  if (pageRoot) mvStayController = bindMvStayFromDom(pageRoot)
})

onBeforeUnmount(() => {
  mvStayController?.destroy()
  mvStayController = undefined
  pageScripts?.destroy()
  if (statusTimer) window.clearTimeout(statusTimer)
  document.documentElement.classList.remove('digital-live-page-active', 'digital-live-touch')
})
</script>

<template>
  <main class="digital-live-page">
    <SiteNavbar
      :contact-label="isContactVisible ? CONTACT_EMAIL : '联系我们'"
      :copy-status="copyStatus"
      @contact="copyContact"
    />
    <HeroSection :is-mobile="isMobile" />
    <IconMarqueeSection v-if="!isMobile" />
    <CapabilitySection
      :capabilities="CAPABILITIES"
      :is-mobile="isMobile"
    />
    <CtaBannerSection :is-mobile="isMobile" />
    <WhyDigitalHumanSection />
    <WhyMliveSection />
    <CaseSection :is-mobile="isMobile" />
    <StepSection />
    <TechSection :is-mobile="isMobile" />
    <PatentSection />
    <FaqSection :faq-items="FAQ_ITEMS" />
    <SiteFooter />
    <ClientOnly>
      <SideFloatDock />
    </ClientOnly>
  </main>
</template>
